import {CartInTimeline, isActiveKind} from "@widgets/reports/cartTimeline/CartTimelineUtils";

/** Key figures of the carts of the ordering site (POS orders left out). */
export type CartTimelineFigures = {
    active: number,
    paying: number,
    started: number,
    ordered: number,
    /** Percentage of the started carts that is ordered, `null` without carts. */
    conversion: number | null,
    abandoned: number,
    /** Average seconds from creating the cart to placing the order, `null` without orders. */
    averageSecondsToOrder: number | null,
    /** Total value of the placed orders. */
    revenue: number,
    /** `null` without orders. */
    averageOrderValue: number | null,
    withAccount: number,
    withoutAccount: number,
    /** Percentage of the started carts with an account, `null` without carts. */
    accountShare: number | null,
    delivery: number,
    takeaway: number,
    /** Percentage of the started carts for delivery, `null` without carts. */
    deliveryShare: number | null,
};

/** Rounded percentage of `part` in `total`, `null` when the total is 0. */
const percentage = (part: number, total: number): number | null =>
    total === 0 ? null : Math.round(part / total * 100);

const average = (values: number[]): number | null =>
    values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;

export const cartTimelineFigures = (carts: CartInTimeline[]): CartTimelineFigures => {
    const websiteCarts: CartInTimeline[] = carts.filter(c => c.kind !== "pos");
    const active: CartInTimeline[] = websiteCarts.filter(c => isActiveKind(c.kind));
    const ordered: CartInTimeline[] = websiteCarts.filter(c => c.kind === "ordered");
    // Only orders with their order time: the bar then ends when ordered
    const placedOrders: CartInTimeline[] = ordered.filter(c => !!c.cart.order);
    const withAccount: number = websiteCarts.filter(c => c.cart.userId !== null).length;
    const delivery: number = websiteCarts.filter(c => c.cart.isDelivery).length;
    const averageSecondsToOrder: number | null =
        average(placedOrders.map(c => Math.max(0, (c.end.getTime() - c.start.getTime()) / 1000)));
    const orderValues: number[] = placedOrders.map(c => c.cart.order!.totalToPay);

    return {
        active: active.length,
        paying: active.filter(c => c.kind === "paying").length,
        started: websiteCarts.length,
        ordered: ordered.length,
        conversion: percentage(ordered.length, websiteCarts.length),
        abandoned: websiteCarts.filter(c => c.kind === "expired" || c.kind === "reset").length,
        averageSecondsToOrder: averageSecondsToOrder === null ? null : Math.round(averageSecondsToOrder),
        revenue: orderValues.reduce((sum, value) => sum + value, 0),
        averageOrderValue: average(orderValues),
        withAccount,
        withoutAccount: websiteCarts.length - withAccount,
        accountShare: percentage(withAccount, websiteCarts.length),
        delivery,
        takeaway: websiteCarts.length - delivery,
        deliveryShare: percentage(delivery, websiteCarts.length),
    };
};

/** Duration as minutes and seconds, e.g. 754 -> `12:34`. */
export const formatMinutesSeconds = (seconds: number): string =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;